import React, { useState, useEffect, useRef } from "react";
import PropTypes from "prop-types";
import { Grid, Typography, Box } from "@mui/material";
import dayjs from "dayjs";

function DateRangePicker({
  startDate,
  setStartDate,
  endDate,
  setEndDate,
  setTimePeriod,
  setTarget,
  selectedTab: propSelectedTab,
  setSelectedTab: propSetSelectedTab,
  variant = "default",
  disabled = false,
}) {
  const [localSelectedTab, setLocalSelectedTab] = useState(0);
  const selectedTab = propSelectedTab !== undefined ? propSelectedTab : localSelectedTab;
  const setSelectedTab = propSetSelectedTab || setLocalSelectedTab;
  const isInitialized = useRef(false);

  const defaultDateRanges = [
    { label: "Past 4 Weeks", value: "sameMonthLY", timePeriod: "months", target: 12 },
    { label: "Past 3 Months", value: "thisQuarter", timePeriod: "quarters", target: 4 },
    { label: "MAT24", value: "last12Months", timePeriod: "P12M", target: null },
  ];

  const yearlyDateRanges = [
    { label: "Last Year", value: "lastYear", timePeriod: "P12M" },
    { label: "Last 2 Years", value: "last2Years", timePeriod: "P24M" },
    { label: "Last 3 Years", value: "last3Years", timePeriod: "P36M" },
  ];

  const dateRanges = variant === "yearly" ? yearlyDateRanges : defaultDateRanges;

  const calculateDateRange = (rangeType) => {
    const today = dayjs();
    const endDate = dayjs("31 December 2024", "MMM DD, YYYY");
    console.log(endDate)

    switch (rangeType) {
      case "lastYear":
        return {
          start: dayjs("2024-01-01", "YYYY-MM-DD").startOf("year"),
          end: dayjs("2024-12-31", "YYYY-MM-DD").endOf("year"),
        };

      case "last2Years":
        return {
          start: dayjs("2023-01-01", "YYYY-MM-DD").startOf("year"),
          end: dayjs("2024-12-31", "YYYY-MM-DD").endOf("year"),
        };
          
      case "last3Years":
        return {
          start: dayjs("2022-01-01", "YYYY-MM-DD").startOf("year"),
          end: dayjs("2024-12-31", "YYYY-MM-DD").endOf("year"),
        };    

      case "sameMonthLY":
        return {
          start: endDate.subtract(4, "weeks").startOf("month"),
          end: endDate.endOf("month"),
        };

      case "thisQuarter":
        return {
          start: endDate.subtract(2, "month").startOf("month"),
          end: endDate.endOf("month"),
        };

      case "last12Months":
        return {
          start: endDate.subtract(11, "month").startOf("month"),
          end: endDate.endOf("month"),
        };

      default:
        return {
          start: today.subtract(1, "year"),
          end: today,
        };
    }
  };

  useEffect(() => {
    if (!isInitialized.current || selectedTab !== undefined) {
      const initialRange = dateRanges[selectedTab] || dateRanges[0];
      if (variant === "yearly") {
        if (setTimePeriod) setTimePeriod(initialRange.timePeriod);
      } else {
        const { start, end } = calculateDateRange(initialRange.value);
        if (!startDate || !start.isSame(startDate) || !end.isSame(endDate)) {
          if (setStartDate) setStartDate(start);
          if (setEndDate) setEndDate(end);
          if (setTimePeriod) setTimePeriod(initialRange.timePeriod);
          if (setTarget && initialRange.target !== undefined) setTarget(initialRange.target);
        }
      }
      isInitialized.current = true;
    }
  }, [selectedTab]);

  useEffect(() => {
    console.log("🔄 startDate updated:", startDate?.format("YYYY-MM-DD"));
    console.log("🔄 endDate updated:", endDate?.format("YYYY-MM-DD"));
  }, [startDate, endDate]);

  const handleTabChange = (event, newValue) => {
    if (disabled) return;
    setSelectedTab(newValue);
    const selectedRange = dateRanges[newValue];
    if (variant === "yearly") {
      if (setTimePeriod) setTimePeriod(selectedRange.timePeriod);
    } else {
      const { start, end } = calculateDateRange(selectedRange.value);
      if (setStartDate) setStartDate(start);
      if (setEndDate) setEndDate(end);
      if (setTimePeriod) {
        setTimePeriod(selectedRange.timePeriod);
        if (setTarget) setTarget(selectedRange.target);
      }
    }
  };

  return (
    <Grid 
      container 
      spacing={{ xs: 1, sm: 2 }} 
      alignItems="center" 
      justifyContent="center"
    >
      <Grid item xs={12}>
        <Box
          sx={{
            display: "flex",
            flexWrap: "wrap",
            justifyContent: "center",
            alignItems: "center",
            rowGap: { xs: "4px", sm: "6px", md: "8px" },
            columnGap: { xs: "6px", sm: "8px", md: "12px" },
            border: "1px solid #ccc",
            borderRadius: { xs: "12px", sm: "16px", md: "20px" },
            maxWidth: "fit-content",
            boxShadow: "0 1px 3px rgba(0,0,0,0.05)",
            margin: "auto",
            opacity: disabled ? 0.5 : 1,
            pointerEvents: disabled ? "none" : "auto",
          }}
        >
          {dateRanges.map((range, index) => (
            <Box
              key={range.value}
              onClick={() => handleTabChange(null, index)}
              sx={{
                padding: { 
                  xs: "6px 10px", 
                  sm: "7px 12px", 
                  md: "8px 16px" 
                },
                borderRadius: { xs: "12px", sm: "16px", md: "20px" },
                cursor: disabled ? "not-allowed" : "pointer",
                fontSize: { 
                  xs: "11px", 
                  sm: "12px", 
                  md: "13px", 
                  lg: "14px" 
                },
                fontWeight: "500",
                textTransform: "none",
                transition: "all 0.2s ease",
                backgroundColor:
                  selectedTab === index ? "#05004E" : "transparent",
                color: selectedTab === index ? "white" : "#666",
                border: "none",
                "&:hover": disabled ? {} : {
                  backgroundColor:
                    selectedTab === index ? "#05004E" : "#f5f5f5",
                },
                userSelect: "none",
                whiteSpace: "nowrap",
              }}
            >
              {range.label}
            </Box>
          ))}
        </Box>

        {variant !== "yearly" && (
          <Typography
            variant="caption"
            color="text.secondary"
            sx={{
              mt: { xs: 1, sm: 1.5, md: 2 },
              display: "block",
              fontSize: { xs: "10px", sm: "11px", md: "12px" },
              textAlign: "center",
            }}
          >
            {`${startDate?.format("MMM DD, YYYY")} - ${endDate?.format("MMM DD, YYYY")}`}
          </Typography>
        )}
      </Grid>
    </Grid>
  );
}

DateRangePicker.propTypes = {
  startDate: PropTypes.object,
  setStartDate: PropTypes.func,
  endDate: PropTypes.object,
  setEndDate: PropTypes.func,
  setTimePeriod: PropTypes.func,
  setTarget: PropTypes.func,
  selectedTab: PropTypes.number,
  setSelectedTab: PropTypes.func,
  variant: PropTypes.string,
  disabled: PropTypes.bool,
};

export default DateRangePicker;